import React from 'react';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { AlertCircle, InfoIcon } from 'lucide-react';
import { 
  Tooltip, 
  TooltipContent, 
  TooltipTrigger,
  TooltipProvider 
} from '@/components/ui/tooltip';

interface BacktestProviderToggleProps {
  provider: 'polygon' | 'alpaca';
  onProviderChange: (provider: 'polygon' | 'alpaca') => void;
}

export const BacktestProviderToggle: React.FC<BacktestProviderToggleProps> = ({
  provider,
  onProviderChange
}) => {
  const handleToggleChange = (checked: boolean) => {
    onProviderChange(checked ? 'alpaca' : 'polygon');
  };

  return (
    <div className="flex flex-col space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <Label htmlFor="data-provider" className="text-base font-semibold mr-2">
            Data Provider
          </Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <InfoIcon className="h-4 w-4 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p>Choose between Polygon.io or Alpaca for market data.</p>
                <p className="mt-1 text-xs font-medium text-green-600">
                  Alpaca (Recommended): Full historical data available
                </p>
                <p className="mt-1 text-xs text-yellow-600">
                  Polygon.io (Free Tier): Limited to last 30 days of data
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="flex items-center space-x-2">
          <span className={`text-sm ${provider === 'polygon' ? 'font-medium text-yellow-600' : 'text-muted-foreground'}`}>
            Polygon.io
          </span>
          <Switch
            id="data-provider"
            checked={provider === 'alpaca'}
            onCheckedChange={handleToggleChange}
            className={provider === 'alpaca' ? 'bg-green-600 data-[state=checked]:bg-green-600' : ''}
          />
          <span className={`text-sm ${provider === 'alpaca' ? 'font-medium text-green-600' : 'text-muted-foreground'}`}>
            Alpaca
          </span>
        </div>
      </div>
      
      {provider === 'polygon' && (
        <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-yellow-600 mr-2 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-yellow-800">Limited Data Access (Not Recommended)</p>
              <p className="text-xs text-yellow-700">
                <strong>Warning:</strong> Free Polygon.io plan provides limited access to market data.
                Most backtests will fail due to data limitations.
                <strong className="block mt-1 text-green-700">We recommend switching to Alpaca data which is already available with your account.</strong>
              </p>
            </div>
          </div>
        </div>
      )}
      
      {provider === 'alpaca' && (
        <div className="p-3 bg-green-50 rounded-lg border border-green-200">
          <div className="flex">
            <svg className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <div>
              <p className="text-sm font-medium text-green-800">Using Alpaca Market Data (Recommended)</p>
              <p className="text-xs text-green-700">
                Full historical data access with your existing Alpaca account.
                Perfect for comprehensive backtesting with reliable results.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};